import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  OnChanges,
  AfterViewInit,
  ViewChild,
  ElementRef,
  Input,
  DestroyRef,
  inject,
  signal,
} from "@angular/core";
//import { MatPaginator, MatSort, MatTableDataSource } from '@angular/material';
import { MatSort } from "@angular/material/sort";
import { MatPaginatorModule } from "@angular/material/paginator";
import { MatTableDataSource } from "@angular/material/table";
import { MatPaginator } from "@angular/material/paginator";
import { ActivatedRoute, Router, RouterLinkWithHref } from "@angular/router";
import {
  MatDialogModule,
  MatDialog,
  MatDialogRef,
  MAT_DIALOG_DATA,
  MatDialogConfig,
} from "@angular/material/dialog";
import { MatMenuTrigger } from "@angular/material/menu";
import { MsalService } from "@azure/msal-angular";
import { MatIconRegistry } from "@angular/material/icon";
import { DomSanitizer } from "@angular/platform-browser";

// Services
import { StoreUserService } from '@app/core/state/store-user.service';
import { ListSystemAdminsService } from "../../../data/system-admin/list-system-admins.service";
import { DatabaseCacheService } from "../../../data/cache/database-cache.service";
import { SystemAdminDetailMode } from "../system-admin-details/system-admin-details.component";

import dayjs from "dayjs";

// For Dialog view
import {
  SystemAdminDetailsComponent,
  SysAdminData,
} from "../system-admin-details/system-admin-details.component";
import { SHARED_IMPORTS } from "src/app/shared/standalone-imports";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { TableSkeletonComponent } from "../../../shared/skeleton";
import { PageHeaderComponent } from "../../../shared/components/page-header/page-header.component";
import { SYSTEM_ADMIN_LIST_I18N } from "./system-admin-list.i18n";

@Component({
  selector: "app-system-admin-list",
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: "./system-admin-list.component.html",
  styleUrls: ["./system-admin-list.component.scss"],
  imports: [...SHARED_IMPORTS, TableSkeletonComponent, PageHeaderComponent],
})
export class SystemAdminListComponent implements OnInit, AfterViewInit {
  private readonly destroyRef = inject(DestroyRef);

  // i18n定数（型安全・IDE補完対応）
  protected readonly i18n = SYSTEM_ADMIN_LIST_I18N;

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;
  @ViewChild("menuTrigger") menuTrigger: MatMenuTrigger;
  @ViewChild("searchInput") searchInput: ElementRef<HTMLInputElement>;

  loginUserId = "";
  filter = ""; //table filtering
  isLoading = signal(true);
  readonly searchExpanded = signal(false);

  sectionGroupId;
  hasSystemAdminManagement;

  admins: SysAdminData[] = [];
  displayedColumns = ["name", "status", "assignedDate"];

  accountStatus: { [key: string]: string } = {
    OPEN: SYSTEM_ADMIN_LIST_I18N.status.active,
    CLOSE: SYSTEM_ADMIN_LIST_I18N.status.inactive,
  };

  //dialog with path param
  private dialogRef: MatDialogRef<any, any>;
  private routerUrl: string;

  dataSource: MatTableDataSource<SysAdminData>;

  constructor(
    public storeUser: StoreUserService,
    private route: ActivatedRoute,
    private listSystemAdminsService: ListSystemAdminsService,
    private router: Router,
    private msalService: MsalService,
    public dialog: MatDialog,
    private domSanitizer: DomSanitizer,
    private iconRegistry: MatIconRegistry,
    private common: DatabaseCacheService,
  ) {
    let user = this.msalService.instance.getActiveAccount();
    this.loginUserId = user.idTokenClaims["sub"];

    // カスタムSVG iconの呼び出し
    this.iconRegistry.addSvgIcon(
      "icon_staff", // ここで名前を指定
      this.domSanitizer.bypassSecurityTrustResourceUrl(
        "./assets/icon_staff.svg",
      ), //必ずサニタイザーを利用して取得する。
    );

    this.iconRegistry.addSvgIcon(
      "icon_setting", // ここで名前を指定
      this.domSanitizer.bypassSecurityTrustResourceUrl(
        "./assets/icon_setting.svg",
      ),
    );

    this.iconRegistry.addSvgIcon(
      "icon_account-close", // ここで名前を指定
      this.domSanitizer.bypassSecurityTrustResourceUrl(
        "./assets/icon_account-close.svg",
      ),
    );

    this.iconRegistry.addSvgIcon(
      "icon_account-open", // ここで名前を指定
      this.domSanitizer.bypassSecurityTrustResourceUrl(
        "./assets/icon_account-open.svg",
      ),
    );
  }

  ngOnInit() {
    //Admin の一覧で表示を制御する
    this.displayedColumns = ["name", "status", "assignedDate"];

    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      this.listSysAdmins();
      this.hasSystemAdminManagement = this.common.hasSystemAdminManagement();
    });

    this.common.parametersUpdated.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((noData) => {});
  }

  ngAfterViewInit() {}

  applyFilter(event?: Event) {
    if (event) {
      this.filter = (event.target as HTMLInputElement).value;
    }
    const filterValue = this.filter.trim().toLowerCase();
    this.dataSource.filter = filterValue;
  }

  // M3 Expandable Search methods
  expandSearch(): void {
    this.searchExpanded.set(true);
    setTimeout(() => {
      this.searchInput?.nativeElement?.focus();
    }, 50);
  }

  collapseSearch(): void {
    if (!this.filter.trim()) {
      this.searchExpanded.set(false);
    }
  }

  onSearchBlur(): void {
    this.collapseSearch();
  }

  clearSearch(event: Event): void {
    event.stopPropagation();
    this.filter = "";
    this.applyFilter();
  }

  inviteAdmin() {
    const dialogRef = this.dialog.open(SystemAdminDetailsComponent, {
      width: "740px",
      disableClose: false,

      data: {
        title: this.i18n.dialog.confirmTitle,
        message: this.i18n.dialog.confirmMessage,
        sysAdminData: null,
        mode: SystemAdminDetailMode.invite,
      },
    });
    dialogRef.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((result) => {});
  }

  opeAdminDetailsDialog(res): void {

    const dialogRef = this.dialog.open(SystemAdminDetailsComponent, {
      width: "740px",
      disableClose: false,

      data: {
        title: this.i18n.dialog.confirmTitle,
        message: this.i18n.dialog.confirmMessage,
        sysAdminData: res,
        mode: SystemAdminDetailMode.disp,
      },
    });
    dialogRef.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(async (result) => {
      if (result?.needRefresh) {
        await this.common.refreshAllData();
        this.listSysAdmins();
      }
    });
  }

  refreshData(): void {
    this.listSysAdmins();
  }

  listSysAdmins() {
    this.isLoading.set(true);

    this.listSystemAdminsService.listSystemAdmins().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (res) => {
        this.admins = [];
        for (let i = 0; i < (res as any).length; i++) {
          let data = (res as any)[i];

          let userId = data.user?.userId;
          let firstName = data.user?.firstName;
          let lastName = data.user?.lastName;
          let userName = lastName + " " + firstName;
          let roles = data.assignment?.roles;
          let assignedData = data.assignment?.commonInfo.createdAt;
          let lastMofifiedAt = data.assignment?.commonInfo.lastModifiedAt;
          let lastModifiedBy = data.assignment?.commonInfo.lastModifiedBy;

          let admin: SysAdminData = {
            userId: data.user.userId,

            lastName: lastName,
            firstName: firstName,
            name: userName,

            roles: roles,

            status: data.assignment?.status.toUpperCase(),
            assignedDate: dayjs(assignedData).isValid()
              ? dayjs(assignedData).format("YYYY/MM/DD")
              : null,

            lastModifiedAt: dayjs(lastMofifiedAt).isValid()
              ? dayjs(lastMofifiedAt).format("YYYY/MM/DD")
              : null,
            lastModifiedBy: lastModifiedBy,
          };

          if (userId === this.loginUserId) {
            this.admins.unshift(admin);
          } else {
            this.admins.push(admin);
          }
        }

        this.dataSource = new MatTableDataSource(this.admins);
        this.dataSource.paginator = this.paginator;
        this.dataSource.sort = this.sort;

        this.isLoading.set(false);
      },
      (error) => {
        this.isLoading.set(false);
      },
    );
  }
}
